/* SafeStap · customer reviews: show more, rating/photo filter, write-a-review dialog, CSV import. */
if (!customElements.get('ss-reviews')) {
  const STAR_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2.6l2.83 5.94 6.53.79-4.8 4.5 1.24 6.47L12 17.13l-5.8 3.17 1.24-6.47-4.8-4.5 6.53-.79z"/></svg>';
  const VERIFIED_SVG =
    '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M6 10.3l2.6 2.6L14.2 7.3" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const MAX_ROWS = 2000;

  // Accepted header names (lowercase, spaces and dashes treated as underscores).
  const COLUMNS = {
    first: ['first_name', 'firstname', 'first', 'given_name', 'prenom', 'prénom', 'name', 'customer_name', 'author', 'reviewer_name'],
    last: ['last_name', 'lastname', 'last', 'surname', 'family_name', 'nom_de_famille', 'nom'],
    content: ['content', 'review', 'review_content', 'body', 'text', 'message', 'comment', 'avis', 'contenu', 'avis_en'],
    image: ['image', 'image_url', 'photo', 'photo_url', 'picture', 'picture_url', 'media', 'image_1'],
    rating: ['rating', 'stars', 'star_rating', 'score', 'note', 'etoiles', 'étoiles'],
    location: ['location', 'city', 'state', 'country', 'ville', 'lieu'],
    date: ['date', 'review_date', 'created_at'],
    title: ['title', 'review_title', 'headline', 'titre'],
    verified: ['verified', 'verified_buyer', 'verified_purchase', 'verifie', 'vérifié'],
  };

  const parseCsv = (input) => {
    const text = input.replace(/^﻿/, '');
    const firstLine = text.split(/\r?\n/, 1)[0] || '';
    const delimiter = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      if (quoted) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i += 1;
          } else {
            quoted = false;
          }
        } else {
          field += char;
        }
      } else if (char === '"') {
        quoted = true;
      } else if (char === delimiter) {
        row.push(field);
        field = '';
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && text[i + 1] === '\n') i += 1;
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else {
        field += char;
      }
    }
    if (field !== '' || row.length) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
  };

  const columnIndex = (headers, key) => {
    const normalized = headers.map((header) => header.trim().toLowerCase().replace(/[\s-]+/g, '_'));
    for (const alias of COLUMNS[key]) {
      const index = normalized.indexOf(alias);
      if (index !== -1) return index;
    }
    return -1;
  };

  const isYes = (value) => /^(1|true|yes|y|oui|x)$/i.test(String(value || '').trim());

  class SsReviews extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.ss-rv__grid');
      this.cards = Array.from(this.querySelectorAll('.ss-rv__card'));
      this.moreButton = this.querySelector('.ss-rv__more');
      this.emptyMessage = this.querySelector('.ss-rv__empty');
      this.filterSelect = this.querySelector('.ss-rv__filter');
      this.dialog = this.querySelector('.ss-rv__dialog');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.step = parseInt(this.dataset.step, 10) || 8;

      this.onReset = () => this.reset();
      this.onBlockSelect = (event) => {
        const card = event.target.closest && event.target.closest('.ss-rv__card');
        if (card && this.contains(card)) card.hidden = false;
      };

      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => {
          this.limit += this.step;
          this.render();
        });
      }
      if (this.filterSelect) this.filterSelect.addEventListener('change', this.onReset);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onReset);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.querySelectorAll('[data-open-dialog]').forEach((button) => {
        button.addEventListener('click', () => this.openDialog());
      });
      if (this.dialog) {
        this.dialog.querySelectorAll('[data-close]').forEach((button) => {
          button.addEventListener('click', () => this.dialog.close());
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        if (this.dialog.querySelector('[data-open-on-load]')) this.openDialog();
      }

      this.reset();
      if (this.dataset.csvUrl) this.loadCsv();
    }

    disconnectedCallback() {
      if (this.mobileQuery && this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onReset);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    initialLimit() {
      const value = this.mobileQuery.matches ? this.dataset.initialMobile : this.dataset.initialDesktop;
      return parseInt(value, 10) || 8;
    }

    currentFilter() {
      return this.filterSelect ? this.filterSelect.value : 'all';
    }

    matches(card) {
      const filter = this.currentFilter();
      if (filter === 'all') return true;
      if (filter === 'photos') return card.dataset.photo === 'true';
      return card.dataset.rating === filter;
    }

    reset() {
      this.limit = this.initialLimit();
      if (this.filterSelect) {
        const wrap = this.filterSelect.closest('.ss-rv__filter-wrap');
        if (wrap) wrap.classList.toggle('is-active', this.currentFilter() !== 'all');
      }
      this.render();
    }

    render() {
      let shown = 0;
      let total = 0;
      this.cards.forEach((card) => {
        const match = this.matches(card);
        if (match) total += 1;
        const visible = match && shown < this.limit;
        if (visible) shown += 1;
        card.hidden = !visible;
      });
      if (this.moreButton) this.moreButton.hidden = shown >= total;
      if (this.emptyMessage) this.emptyMessage.hidden = total > 0;
    }

    openDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    /* ---------- CSV import ---------- */

    async loadCsv() {
      try {
        const response = await fetch(this.dataset.csvUrl, { credentials: 'omit' });
        if (!response.ok) throw new Error(`CSV request failed (${response.status})`);
        const rows = parseCsv(await response.text());
        const cards = this.cardsFromRows(rows);
        if (this.grid && cards.length) {
          const fragment = document.createDocumentFragment();
          cards.forEach((card) => fragment.appendChild(card));
          if (this.dataset.csvPosition === 'before') {
            this.grid.insertBefore(fragment, this.grid.firstChild);
          } else {
            this.grid.appendChild(fragment);
          }
        }
      } catch (error) {
        console.warn('[SafeStep reviews] Could not import the CSV file:', error);
      }
      this.cards = Array.from(this.querySelectorAll('.ss-rv__card'));
      this.updateSummary();
      this.removeAttribute('data-csv-pending');
      this.reset();
    }

    cardsFromRows(rows) {
      if (rows.length < 2) return [];
      const headers = rows[0];
      const index = {};
      Object.keys(COLUMNS).forEach((key) => {
        index[key] = columnIndex(headers, key);
      });
      // "name" alone is used as the first name; avoid reading the same column twice.
      if (index.last === index.first) index.last = -1;

      const cell = (row, key) => (index[key] === -1 ? '' : String(row[index[key]] || '').trim());
      return rows
        .slice(1, MAX_ROWS + 1)
        .map((row) => {
          const review = {
            first: cell(row, 'first'),
            last: cell(row, 'last'),
            content: cell(row, 'content'),
            image: cell(row, 'image'),
            rating: parseFloat(cell(row, 'rating').replace(',', '.')),
            location: cell(row, 'location'),
            date: cell(row, 'date'),
            title: cell(row, 'title'),
            verified: this.dataset.csvVerified === 'true' || isYes(cell(row, 'verified')),
          };
          if (!review.content && !review.title) return null;
          return this.buildCard(review);
        })
        .filter(Boolean);
    }

    displayName(first, last) {
      const format = this.dataset.nameFormat || 'first_initial';
      if (!first) return last;
      if (format === 'first' || !last) return first;
      if (format === 'full') return `${first} ${last}`.trim();
      return `${first} ${last.charAt(0).toUpperCase()}.`.trim();
    }

    imageUrl(value) {
      if (!value) return '';
      let url = value;
      if (!/^(https?:)?\/\//i.test(url) && !url.startsWith('/')) {
        url = (this.dataset.filesBase || '') + encodeURIComponent(url);
      }
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) {
        url += (url.includes('?') ? '&' : '?') + 'width=900';
      }
      return url;
    }

    buildCard(review) {
      const el = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text) node.textContent = text;
        return node;
      };

      const rating = Number.isFinite(review.rating) ? Math.min(5, Math.max(1, Math.round(review.rating))) : 0;
      const name = this.displayName(review.first, review.last);
      const imageSrc = this.imageUrl(review.image);

      const card = el('article', 'ss-rv__card');
      card.dataset.rating = rating ? String(rating) : '';
      card.dataset.photo = imageSrc ? 'true' : 'false';
      card.dataset.imported = 'true';

      if (imageSrc) {
        const photo = el('div', 'ss-rv__photo');
        const img = document.createElement('img');
        img.src = imageSrc;
        img.alt = name ? `Photo from ${name}` : 'Customer photo';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('error', () => {
          photo.remove();
          card.dataset.photo = 'false';
        });
        photo.appendChild(img);
        card.appendChild(photo);
      }

      const body = el('div', 'ss-rv__body');
      const author = el('div', 'ss-rv__author');
      const who = el('div', 'ss-rv__who');
      const nameLine = el('p', 'ss-rv__name');
      nameLine.appendChild(el('span', '', name));
      if (review.verified && this.dataset.showVerified === 'true') {
        const badge = el('span', 'ss-rv__verified');
        badge.innerHTML = VERIFIED_SVG;
        badge.appendChild(document.createTextNode(` ${this.dataset.verifiedLabel || ''}`));
        nameLine.appendChild(badge);
      }
      who.appendChild(nameLine);

      const showLocation = this.dataset.showLocation === 'true' && review.location;
      const showDate = this.dataset.showDates === 'true' && review.date;
      if (showLocation || showDate) {
        const meta = el('p', 'ss-rv__meta');
        if (showLocation) meta.appendChild(el('span', '', review.location));
        if (showLocation && showDate) {
          const dot = el('span', '', ' · ');
          dot.setAttribute('aria-hidden', 'true');
          meta.appendChild(dot);
        }
        if (showDate) meta.appendChild(el('span', '', review.date));
        who.appendChild(meta);
      }
      author.appendChild(who);
      body.appendChild(author);

      if (rating) {
        const stars = el('span', 'ss-rv__stars');
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        for (let i = 1; i <= 5; i += 1) {
          const star = el('span', i <= rating ? 'is-on' : '');
          star.innerHTML = STAR_SVG;
          stars.appendChild(star);
        }
        body.appendChild(stars);
      }

      if (review.title) body.appendChild(el('p', 'ss-rv__title', review.title));
      if (review.content) {
        const text = el('p', 'ss-rv__text');
        review.content.split(/\r?\n/).forEach((line, i) => {
          if (i) text.appendChild(document.createElement('br'));
          text.appendChild(document.createTextNode(line));
        });
        body.appendChild(text);
      }

      card.appendChild(body);
      return card;
    }

    updateSummary() {
      const total = this.cards.length;
      const rated = this.cards.map((card) => parseInt(card.dataset.rating, 10)).filter((n) => n >= 1 && n <= 5);
      const average = rated.length ? Math.round((rated.reduce((sum, n) => sum + n, 0) / rated.length) * 10) / 10 : 0;
      const countFixed = this.dataset.countFixed === 'true';
      const averageFixed = this.dataset.averageFixed === 'true';

      if (!countFixed) {
        const label = this.dataset.countLabel || '';
        const countText = label.includes('[count]') ? label.replace('[count]', total) : `${total} ${label}`.trim();
        this.querySelectorAll('.ss-rv__summary-count, .ss-rv__count').forEach((node) => {
          node.textContent = countText;
        });
      }

      if (!averageFixed) {
        const averageText = average.toFixed(1);
        this.querySelectorAll('.ss-rv__summary-avg').forEach((node) => {
          node.textContent = averageText;
        });
        this.querySelectorAll('.ss-rv__avg').forEach((node) => {
          if (node.firstChild && node.firstChild.nodeType === Node.TEXT_NODE) node.firstChild.textContent = averageText;
        });
        this.querySelectorAll('.ss-rv__stars--md, .ss-rv__stars--lg').forEach((stars) => {
          stars.setAttribute('aria-label', `${averageText} out of 5 stars`);
          const fill = stars.querySelector('.ss-rv__stars-fill');
          if (fill) fill.style.width = `${average * 20}%`;
        });
      }

      const rows = Array.from(this.querySelectorAll('.ss-rv__bar-row'));
      if (rows.length) {
        const counts = {};
        rows.forEach((row) => {
          const star = row.dataset.star;
          const countNode = row.querySelector('.ss-rv__bar-count');
          counts[star] = row.hasAttribute('data-fixed')
            ? parseInt(countNode && countNode.textContent, 10) || 0
            : rated.filter((n) => String(n) === star).length;
        });
        const barsTotal = Object.values(counts).reduce((sum, n) => sum + n, 0) || 1;
        rows.forEach((row) => {
          const star = row.dataset.star;
          const countNode = row.querySelector('.ss-rv__bar-count');
          const bar = row.querySelector('.ss-rv__bar > span');
          if (countNode) countNode.textContent = counts[star];
          if (bar) bar.style.width = `${Math.min(100, Math.round((counts[star] * 100) / barsTotal))}%`;
        });
      }

      if (!rated.length && !averageFixed) {
        this.querySelectorAll('.ss-rv__summary-score, .ss-rv__score, .ss-rv__bars').forEach((node) => {
          node.hidden = true;
        });
      }
    }
  }

  customElements.define('ss-reviews', SsReviews);
}
